import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Plus,
  GripVertical,
  Trash2,
  Save,
  Eye,
  Type,
  Hash,
  List,
  CheckSquare,
  Image as ImageIcon,
  Calendar,
  ToggleLeft,
  FileText,
  Copy,
  Settings,
} from 'lucide-react';
import { mockFormTemplates } from '../../data/mockData';
import { fetchClients, fetchProducts } from '../../api';

const fieldTypes = [
  { type: 'text', label: 'Text Input', icon: Type },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'dropdown', label: 'Dropdown', icon: List },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { type: 'image', label: 'Image Upload', icon: ImageIcon },
  { type: 'date', label: 'Date Picker', icon: Calendar },
  { type: 'toggle', label: 'Toggle', icon: ToggleLeft },
  { type: 'textarea', label: 'Text Area', icon: FileText },
];

export default function FormBuilder() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showNewFormDialog, setShowNewFormDialog] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [newFormType, setNewFormType] = useState('verification');
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let isMounted = true;
    Promise.all([
      fetchClients().catch(() => []),
      fetchProducts().catch(() => [])
    ]).then(([cRes, pRes]) => {
      if (isMounted) {
        setClients(Array.isArray(cRes) ? cRes : []);
        setProducts(Array.isArray(pRes) ? pRes : []);
      }
    });
    return () => { isMounted = false; };
  }, []);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormFields(template.fields);
  };

  const handleAddField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: `New ${type.charAt(0).toUpperCase() + type.slice(1)} Field`,
      required: false,
      options: type === 'dropdown' ? ['Option 1', 'Option 2'] : undefined,
      maxImages: type === 'image' ? 5 : undefined,
    };
    setFormFields(prev => [...prev, newField]);
  };

  const handleRemoveField = (fieldId) => {
    setFormFields(prev => prev.filter(f => f.id !== fieldId));
  };

  const handleFieldUpdate = (fieldId, updates) => {
    setFormFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  };

  const handleDragStart = (e, index) => {
    setDraggedField(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedField === null || draggedField === index) return;
    
    const newFields = [...formFields];
    const draggedItem = newFields[draggedField];
    newFields.splice(draggedField, 1);
    newFields.splice(index, 0, draggedItem);
    setFormFields(newFields);
    setDraggedField(index);
  };

  const handleDragEnd = () => {
    setDraggedField(null);
  };

  const renderFieldPreview = (field) => {
    switch (field.type) {
      case 'text':
      case 'number':
        return <Input placeholder={field.label} className="bg-slate-50/50 border-slate-200" disabled />;
      case 'textarea':
        return <textarea placeholder={field.label} className="w-full p-2 rounded-md bg-slate-50/50 border border-slate-200 text-slate-500" rows={3} disabled />;
      case 'dropdown':
        return (
          <Select disabled>
            <SelectTrigger className="bg-slate-50/50 border-slate-200">
              <SelectValue placeholder="Select option..." />
            </SelectTrigger>
          </Select>
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox disabled />
            <span className="text-sm text-slate-500">{field.label}</span>
          </div>
        );
      case 'toggle':
        return (
          <div className="flex items-center gap-2">
            <Switch disabled />
            <span className="text-sm text-slate-500">{field.label}</span>
          </div>
        );
      case 'image':
        return (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-slate-500 mb-2" />
            <span className="text-xs text-slate-500">Upload up to {field.maxImages || 5} images</span>
          </div>
        );
      case 'date':
        return <Input type="date" className="bg-slate-50/50 border-slate-200" disabled />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="form-builder-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Form Builder</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage dynamic verification forms</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPreview(true)}
            className="bg-white border-slate-200 gap-2"
            disabled={formFields.length === 0}
            data-testid="preview-form-btn"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            disabled={formFields.length === 0}
            data-testid="save-form-btn"
          >
            <Save className="w-4 h-4" />
            Save Form
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Templates Sidebar */}
        <div className="space-y-4">
          <div className="crm-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Templates</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNewFormDialog(true)}
                className="w-8 h-8 text-slate-500 hover:text-slate-900"
                data-testid="new-template-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {mockFormTemplates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedTemplate?.id === template.id
                        ? 'bg-blue-50 border border-blue-500/30'
                        : 'bg-white hover:bg-slate-100/50 border border-transparent'
                    }`}
                    data-testid={`template-${template.id}`}
                  >
                    <p className="text-sm font-medium text-slate-900">{template.name}</p>
                    <p className="text-[10px] font-semibold text-blue-600 uppercase mt-0.5">{template.type === 'lead' ? 'Lead Creation' : 'Verification'}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {template.client ? template.client.name : 'All Clients'} • {template.fields.length} fields
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Field Types */}
          <div className="crm-card p-4">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Add Field</h3>
            <div className="grid grid-cols-2 gap-2">
              {fieldTypes.map((fieldType) => {
                const Icon = fieldType.icon;
                return (
                  <Button
                    key={fieldType.type}
                    variant="outline"
                    onClick={() => handleAddField(fieldType.type)}
                    className="h-auto py-3 flex-col gap-2 bg-white border-slate-200 hover:border-blue-500/50"
                    data-testid={`add-field-${fieldType.type}`}
                  >
                    <Icon className="w-5 h-5 text-blue-600" />
                    <span className="text-xs">{fieldType.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Builder Area */}
        <div className="lg:col-span-2">
          <div className="crm-card p-6 min-h-[500px]">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedTemplate ? selectedTemplate.name : 'New Form'}
              </h3>
              {selectedTemplate && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase font-semibold text-[10px]">
                    {selectedTemplate.type === 'lead' ? 'Lead Creation' : 'Verification'}
                  </span>
                  <span>{selectedTemplate.product.name}</span>
                  {selectedTemplate.type !== 'lead' && (
                      <>
                        <span>•</span>
                        <span>{selectedTemplate.client?.name || 'All Clients'}</span>
                      </>
                  )}
                </div>
              )}
            </div>

            {formFields.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-500">
                <FileText className="w-12 h-12 mb-4" />
                <p className="text-sm">Select a template or start adding fields</p>
              </div>
            ) : (
              <div className="space-y-4">
                {formFields.map((field, index) => {
                  const FieldIcon = fieldTypes.find(ft => ft.type === field.type)?.icon || Type;
                  return (
                    <div
                      key={field.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`form-field-drag ${draggedField === index ? 'dragging' : ''}`}
                      data-testid={`form-field-${field.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="cursor-grab pt-1">
                          <GripVertical className="w-4 h-4 text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <FieldIcon className="w-4 h-4 text-blue-600" />
                            <Input
                              value={field.label}
                              onChange={(e) => handleFieldUpdate(field.id, { label: e.target.value })}
                              className="flex-1 bg-transparent border-none p-0 h-auto text-sm font-medium text-slate-900 focus-visible:ring-0"
                            />
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setEditingField(field)}
                                className="w-7 h-7 text-slate-500 hover:text-slate-900"
                              >
                                <Settings className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveField(field.id)}
                                className="w-7 h-7 text-slate-500 hover:text-red-400"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          <div className="pl-6">
                            {renderFieldPreview(field)}
                          </div>
                          <div className="flex items-center gap-4 mt-3 pl-6">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`req-${field.id}`}
                                checked={field.required}
                                onCheckedChange={(checked) => handleFieldUpdate(field.id, { required: checked })}
                              />
                              <label htmlFor={`req-${field.id}`} className="text-xs text-slate-500">Required</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        <div className="crm-card p-4">
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Form Properties</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Form Name</Label>
              <Input
                placeholder="Enter form name"
                defaultValue={selectedTemplate?.name}
                className="bg-slate-50/50 border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Form Type</Label>
              <Select defaultValue={selectedTemplate?.type || 'verification'}>
                <SelectTrigger className="bg-slate-50/50 border-slate-200 font-semibold">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="verification">Verification Response</SelectItem>
                  <SelectItem value="lead">Client Lead Creation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(selectedTemplate?.type !== 'lead') && (
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Client Requirement</Label>
                  <Select defaultValue={selectedTemplate?.client?.id}>
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Product</Label>
              <Select defaultValue={selectedTemplate?.product?.id}>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Total Fields</span>
                <span className="text-slate-900 font-medium">{formFields.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-500">Required Fields</span>
                <span className="text-slate-900 font-medium">{formFields.filter(f => f.required).length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Form Preview</DialogTitle>
            <DialogDescription className="text-slate-500">
              This is how the form will appear to users
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {formFields.map((field) => (
              <div key={field.id} className="space-y-2">
                <Label className={field.required ? 'after:content-["*"] after:ml-0.5 after:text-red-500' : ''}>
                  {field.label}
                </Label>
                {renderFieldPreview(field)}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Form Dialog */}
      <Dialog open={showNewFormDialog} onOpenChange={setShowNewFormDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Create New Form</DialogTitle>
            <DialogDescription className="text-slate-500">
              Start building a new verification form
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Form Name</Label>
              <Input placeholder="Enter form name" className="bg-slate-50/50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label>Form Type</Label>
              <Select value={newFormType} onValueChange={setNewFormType}>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select form type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="verification">Verification Response</SelectItem>
                  <SelectItem value="lead">Client Lead Creation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newFormType === 'verification' && (
                <div className="space-y-2">
                  <Label>Client Assignment</Label>
                  <Select>
                    <SelectTrigger className="bg-slate-50/50 border-slate-200">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
            )}
            <div className="space-y-2">
              <Label>Product Type</Label>
              <Select>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFormDialog(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                setSelectedTemplate(null);
                setFormFields([]);
                setShowNewFormDialog(false);
              }}
            >
              Create Form
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
